import React from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  AlertCircle, RefreshCw, Users, CalendarDays, Clock,
  ChevronRight, GraduationCap, BookOpen, Zap, Armchair,
} from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ClassLockedState } from "@/components/teacher/classes/ClassLockedState";
import { useClassManagementHub } from "@/hooks/use-class-management";
import { isReleaseFlagOn } from "@/config/featureFlags";
import { cn } from "@/lib/utils";
import type { HubOffering } from "@/services/classManagementService";
import { useMyOfferings } from "@/hooks/useMyOfferings";
import type { OfferingResponse } from "@/types/offering";

// ── Helpers ───────────────────────────────────────────────────────────────────

function isTierError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as Record<string, unknown>;
  if ((e["status"] as number) === 403) return true;
  const response = e["response"] as Record<string, unknown> | undefined;
  return (response?.["status"] as number) === 403;
}

function formatNextSession(iso: string | null): string {
  if (!iso) return "No upcoming session";
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 0) return `Today · ${time}`;
  if (diffDays === 1) return `Tomorrow · ${time}`;
  if (diffDays < 7) return `${d.toLocaleDateString([], { weekday: "long" })} · ${time}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${time}`;
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function HubSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-3 flex-1">
              <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-2/5" />
                <Skeleton className="h-3 w-1/5" />
              </div>
            </div>
            <Skeleton className="h-8 w-24 rounded-lg shrink-0" />
          </div>
          <div className="flex gap-5 pt-3 border-t border-slate-100">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3.5 w-36" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Offering card ─────────────────────────────────────────────────────────────

function OfferingCard({ offering, fullOffering }: { offering: HubOffering; fullOffering?: OfferingResponse }) {
  const navigate = useNavigate();

  const typeLabel =
    offering.offeringType === "ONE_TO_ONE" ? "1-on-1" :
    offering.offeringType === "GROUP"       ? "Group"  : "Crash Course";

  const TypeIcon =
    offering.offeringType === "ONE_TO_ONE" ? GraduationCap :
    offering.offeringType === "GROUP"       ? Users         : Zap;

  // Extra metadata from full offering (if loaded)
  const sessionDuration =
    fullOffering?.oneToOne?.sessionDurationMinutes ??
    fullOffering?.group?.sessionDurationMinutes ??
    fullOffering?.crashCourse?.sessionDurationMinutes ??
    null;
  const sessionsPerWeek =
    offering.scheduleHint?.sessionsPerWeek ??
    fullOffering?.group?.sessionsPerWeek ??
    fullOffering?.crashCourse?.sessionsPerWeek ??
    null;
  const seatsRemaining = fullOffering?.computedFields?.seatsRemaining ?? null;
  const isGroupOrCrash = offering.offeringType === "GROUP" || offering.offeringType === "CRASH_COURSE";

  return (
    <div
      role="article"
      aria-label={offering.title}
      data-testid={`class-card-${offering.offeringId}`}
      className="bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={() => navigate(`/teacher-classes/${offering.offeringId}`)}
    >
      <div className="p-5">
        {/* Top row: icon + title + manage button */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
              <TypeIcon className="w-5 h-5 text-indigo-600" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 leading-snug truncate">
                {offering.title}
              </h3>
              <Badge className="mt-1 text-xs font-medium bg-slate-100 text-slate-600 border-0 hover:bg-slate-100">
                {typeLabel}
              </Badge>
            </div>
          </div>
          <Button
            size="sm"
            className="shrink-0 cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 transition-colors duration-150"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/teacher-classes/${offering.offeringId}`);
            }}
          >
            Manage
            <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Button>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <Users className="w-4 h-4 text-slate-400" aria-hidden="true" />
            <span className="font-semibold text-slate-800">{offering.confirmedCount}</span>
            <span>confirmed</span>
            {offering.pendingCount > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                {offering.pendingCount} pending
              </span>
            )}
          </div>
          <div className={cn(
            "flex items-center gap-1.5 text-sm",
            offering.nextSessionAt ? "text-slate-600" : "text-slate-400",
          )}>
            <CalendarDays className="w-4 h-4 text-slate-400" aria-hidden="true" />
            <span>{formatNextSession(offering.nextSessionAt)}</span>
          </div>
          {sessionDuration && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <Clock className="w-4 h-4 text-slate-400" aria-hidden="true" />
              <span>{sessionDuration} min</span>
            </div>
          )}
          {sessionsPerWeek && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <CalendarDays className="w-4 h-4 text-slate-400" aria-hidden="true" />
              <span>{sessionsPerWeek}× / week</span>
            </div>
          )}
          {isGroupOrCrash && seatsRemaining !== null && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <Armchair className="w-4 h-4 text-slate-400" aria-hidden="true" />
              <span><span className="font-semibold text-slate-800">{seatsRemaining}</span> seats left</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ClassesHubPage() {
  usePageTitle("Classes | BookMyCoaching");

  const { teacherProfile } = useTeacherProfile();
  const teacherName     = teacherProfile?.fullName    || "";
  const teacherAvatar   = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || [])
    .map((s: any) => typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || "")
    .filter(Boolean);

  const flagOn = isReleaseFlagOn("CLASS_MANAGEMENT");
  const { data, isLoading, isError, error, refetch } = useClassManagementHub();
  const { data: fullOfferings } = useMyOfferings();

  const hasError       = isError || !!error;
  const totalConfirmed = data?.reduce((s, o) => s + o.confirmedCount, 0) ?? 0;
  const totalPending   = data?.reduce((s, o) => s + o.pendingCount,   0) ?? 0;
  const nextSession    = data
    ?.filter((o) => o.nextSessionAt)
    .sort((a, b) => new Date(a.nextSessionAt!).getTime() - new Date(b.nextSessionAt!).getTime())[0]
    ?.nextSessionAt ?? null;

  // ── Body content (varies by state) ─────────────────────────────────────────
  let body: React.ReactNode;

  if (!flagOn) {
    body = <p className="text-sm text-slate-500">Class management is not enabled.</p>;
  } else if (isLoading) {
    body = <HubSkeleton />;
  } else if (hasError && isTierError(error)) {
    body = <div className="flex justify-center py-12"><ClassLockedState /></div>;
  } else if (hasError) {
    body = (
      <div className="flex flex-col items-center gap-4 py-16">
        <AlertCircle className="w-8 h-8 text-slate-400" aria-hidden="true" />
        <p className="text-sm text-slate-600">Something went wrong loading your classes.</p>
        <Button variant="outline" size="sm" className="cursor-pointer gap-2" onClick={() => void refetch()}>
          <RefreshCw className="w-4 h-4" aria-hidden="true" />
          Retry
        </Button>
      </div>
    );
  } else if (!data || data.length === 0) {
    body = (
      <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-2xl border border-slate-200">
        <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
          <BookOpen className="w-7 h-7 text-indigo-400" aria-hidden="true" />
        </div>
        <h3 className="text-sm font-semibold text-slate-800 mb-1">No classes yet</h3>
        <p className="text-sm text-slate-500 max-w-xs">
          Create and publish an offering to start managing your classes here.
        </p>
      </div>
    );
  } else {
    body = (
      <>
        {/* Summary stat pills */}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
            <Users className="w-4 h-4 text-indigo-500" aria-hidden="true" />
            <span className="text-sm font-semibold text-slate-800">{totalConfirmed}</span>
            <span className="text-sm text-slate-500">total students</span>
          </div>
          {totalPending > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 shadow-sm">
              <Clock className="w-4 h-4 text-amber-500" aria-hidden="true" />
              <span className="text-sm font-semibold text-amber-800">{totalPending}</span>
              <span className="text-sm text-amber-700">pending approval</span>
            </div>
          )}
          {nextSession && (
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
              <CalendarDays className="w-4 h-4 text-indigo-500" aria-hidden="true" />
              <span className="text-sm text-slate-500">Next:</span>
              <span className="text-sm font-semibold text-slate-800">{formatNextSession(nextSession)}</span>
            </div>
          )}
        </div>

        {/* Offering cards */}
        <div className="space-y-3">
          {data.map((offering) => (
            <OfferingCard
              key={offering.offeringId}
              offering={offering}
              fullOffering={fullOfferings?.find((o) => o.id === offering.offeringId)}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={teacherName}
            teacherAvatar={teacherAvatar}
            subjects={teacherSubjects}
          />
          <div className="flex-1 flex flex-col min-w-0">
            {/* Page header — border-b runs edge-to-edge, consistent with all other teacher pages */}
            <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0 bg-white">
              <h1 className="text-2xl font-bold text-slate-900">Classes</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Manage your students, sessions, and communications
              </p>
            </div>
            <main className="flex-1 overflow-auto bg-slate-50">
              <div className="px-4 sm:px-6 py-6 max-w-4xl space-y-5">
                {body}
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
